import { Button, FAIcon, IconTooltip, ModuleWrapper } from '@/components'
import type { CalendarNode } from '@/components_deprecated'
import {
  Calendar,
  Card,
  MonthPicker,
  Table,
  UserSelect,
} from '@/components_deprecated'
import {
  enforceHourMinuteString,
  parseFloatToTimestamp,
} from '@/modules/activities'
import { Row } from '@/modules/activities/components/overview/ActivitiesWeekTableRow'
import { PageType } from '@/modules/common/consts'
import { Field, Module } from '@/modules/companies'
import { endOfWeek, format, startOfWeek } from '@/utils/date-fns'
import {
  useConfirm,
  useOnErrorAuto,
  useParam,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { useUserQuery } from '@/utils/hooks/useUser'
import { PERMISSIONS } from '@/utils/permissions'
import { isInvalidDate } from '@/utils/time'
import { useMutation, useQuery } from '@apollo/client'
import { Icon, Loader } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import { AddonOverview } from './AddonOverview'
import {
  addMonths,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  formatISO,
  isSameDay,
  isWithinInterval,
  parseISO,
  setDate,
  startOfDay,
  startOfMonth,
} from 'date-fns'
import React, {
  Activity,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
} from 'react'
import { isMobileOnly } from 'react-device-detect'
import { useNavigate } from 'react-router'
import styled, { useTheme } from 'styled-components'
import { CalendarColorExplanation } from './CalendarColorExplanation'
import {
  DELETE_SALARY_COMMENT_MUTATION,
  DELETE_SALARY_CORRECTION_MUTATION,
  LOCK_CALCULATED_SALARY_FOR_USER,
} from './mutations'
import { WORK_PERIOD_MONTH_REPORT } from './queries'
import { WorkPeriodGroupDetails } from './WorkPeriodGroup/WorkPeriodGroupDetails'
import { WorkPeriodDetails } from './WorkPeriodGroup/WorkPeriodDetails'
import { useSalaryModuleConfiguration } from './hooks/useSalaryModuleConfiguration'
import { useWorkPeriodMigrations } from './hooks/useWorkPeriodMigrations'
import { SalaryCorrectionDialog } from './components/salary-correction-dialog'
import { AbsenceDetailsSalary } from '../timesheets/absence-details/absence-details-salary'
import { SalaryCommentDialog } from './components/salary-comment-dialog'
import { truncate } from '@/utils/string'

const CalendarAndTable = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-areas: 'calendar table';
  gap: 1rem;
  margin-top: 1rem;
  ${props => props.theme.media.mobile} {
    grid-template-columns: 1fr;
    grid-template-areas:
      'calendar'
      'table';
  }
`

const Controls = styled(Card)`
  display: grid;
  grid-template-columns: 1fr auto;

  margin-bottom: 2rem;
  padding: 0;

  section {
    display: grid;
    grid-template-columns: 1fr auto auto auto;
    gap: 1rem;
    padding: 1rem;

    & + section {
      grid-template-columns: auto;
      border-left: 1px solid ${props => props.theme.colors.gray8};
      align-items: center;
    }
  }

  ${props => props.theme.media.mobile} {
    grid-template-columns: 1fr;

    section {
      grid-template-columns: 1fr;
      padding: 1rem;

      & + section {
        padding-top: 0;
        border-left: 0;
      }
    }
  }
`

const StyledTable = styled(Table)`
  tr {
    cursor: default;
  }
`

const StyledRow = styled(Row)`
  height: 14px;

  & > td {
    padding: 1rem 2rem 1rem 1rem !important;
  }
`

const TableArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  grid-area: table;
  min-width: 400px;

  & > p > b {
    cursor: pointer;
    color: ${props => props.theme.colors.primary500};
    text-decoration: underline;
    &:hover {
      text-decoration: none;
    }
  }
`

const CalendarWrapper = styled.div`
  .calendar-block-wrapper > div {
    color: ${props => props.theme.colors.white}!important;
    font-weight: 600;
  }
`

const ApprovalCard = styled(Card)`
  &:hover {
    cursor: pointer;
  }
  margin: 0 0 1rem 0;
`

const FlexWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const UserLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const UserLink = styled.span`
  color: ${props => props.theme.colors.primary500};
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    text-decoration: none;
  }
`

interface WorkPeriodOverviewProps {}

export const WorkPeriodOverview: React.FC<WorkPeriodOverviewProps> = () => {
  const translations = useTranslate({
    salaryCode: 'common.salary-code',
    amount: 'common.amount',
    activities: 'common.activities',
    workDay: 'common.work-day',
    dayRest: 'common.day-rest',
    weekRest: 'common.week-rest',
    showRest: 'common.show-rest',
    export: 'common.export',
    weekN: ['common.week-n', { n: 0 }],
    workOverview: 'salary.work-overview',
    unknownUser: 'common.unknown-user',

    nUsersNeedApproval: ['salary.n-users-need-approval', { n: 0 }],
    oneUserNeedsApproval: 'salary.one-user-needs-approval',
    nWorkPeriods: ['salary.n-work-periods', { n: 0 }],

    sumForMonth: ['activities.sum-for-month-title', { month: '' }],
    driving: 'activities.driving',
    otherWork: 'common.other-work',
    overtime: 'common.overtime',
    undertime: 'common.undertime',
    available: 'activities.availability-time',
    lockedValue: 'salary.locked-value',
    lock: 'salary.lock',
    corrections: 'salary.corrections',

    toast: {
      mailSuccess: 'exports.success-email-process',
    },
    error: {
      generic: 'error.generic-server-error',
    },

    salaryMode: 'common.salary-mode',
    month: 'common.month',
    user: 'common.user',

    overtimeThatOverlapsMonths: 'salary.overtime-that-overlaps-months',
    nextMonth: 'common.next-month',
    workPeriods: 'salary.work-periods',
    addSlashCorrect: 'salary.add-slash-correct',

    delete: 'common.delete-alt',
    doYouWishToDeleteSalaryCorrection:
      'salary.do-you-wish-to-delete-salary-correction',
    doYouWishToDeleteSalaryComment:
      'salary.do-you-wish-to-delete-salary-comment',
    comments: 'common.comments',
  })

  const userId = useParam('userId', true)
  const confirm = useConfirm()

  const navigate = useNavigate()

  const theme = useTheme()
  const me = useUser()
  const onErrorAuto = useOnErrorAuto()
  const { hasPermissions } = usePermissions()
  const canSeeOthers = hasPermissions(
    PERMISSIONS.salary.change.salarybase,
    true
  )

  const salaryConfiguration = useSalaryModuleConfiguration()

  const [activeSalaryCode, setActiveSalaryCode] = useState<string | null>(null)
  const [filterStorageStateLoading, setFilterStorageStateLoading] =
    useState(true)
  const [popupWorkPeriodGroupId, setPopupWorkPeriodGroupId] = useState<
    string | null
  >(null)
  const [remainingApprovalOpen, setRemainingApprovalOpen] = useState(false)

  const {
    formValues: filter,
    updateForm,
    formChangeHandler: handler,
    loadStoredFormState,
    updateInitialValues,
  } = useForm({
    values: {
      user: userId ?? null,
      month: new Date(),
      showAssignedWorkPeriods: false,
    },
    config: {
      callbacks: {
        user: updateUrl,
      },
      storage: {
        storeFormState: true,
        excludeFields: ['user'],
        sessionStorage: true,
        storeFormStateName: 'firmadok__activities-overview-filter',
        transformValues: {
          month: val =>
            typeof val === 'string'
              ? isInvalidDate(new Date(val), new Date())
              : undefined,
        },
      },
    },
  })

  const { user: filterUser, loading: userLoading } = useUserQuery(
    userId,
    Module.ACTIVITIES
  )

  const [startDateCalculation, endDateCalculation] = useMemo((): [
    Date,
    Date,
  ] => {
    // If salary view is active, we need to set interval according to salary start and end dates.
    if (filterUser && filterUser.salaryWorkTime) {
      const baseDate = startOfDay(filter.month)
      const salaryStart = filterUser.salaryWorkTime?.salaryDateStart ?? 1
      const salaryEnd = filterUser.salaryWorkTime?.salaryDateEnd ?? 1

      const forward = 31 - salaryStart > 15

      // What salary period is connected to which month is based on the salary start date.
      if (forward) {
        const startMonthYearTime = setDate(baseDate, salaryStart)
        const endMonthYearTime =
          salaryStart === 1
            ? endOfMonth(baseDate)
            : setDate(addMonths(baseDate, 1), salaryEnd)

        return [startMonthYearTime, endOfDay(endMonthYearTime)]
      }

      const startMonthYearTime = setDate(addMonths(baseDate, -1), salaryStart)
      const endMonthYearTime = setDate(baseDate, salaryEnd)

      return [startMonthYearTime, endOfDay(endMonthYearTime)]
    } else {
      // If salary view is not active, we need to set interval according to the month.
      const startMonthYearTime = startOfMonth(filter.month)
      const endMonthYearTime = endOfMonth(filter.month)

      return [startMonthYearTime, endMonthYearTime]
    }
  }, [filter.month, filterUser])

  const { data, loading, refetch } = useQuery(WORK_PERIOD_MONTH_REPORT, {
    skip: filterStorageStateLoading || userLoading,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    variables: {
      user: userId ?? me.id,
      datetimeStart: formatISO(startDateCalculation),
      datetimeEnd: formatISO(endDateCalculation),
      year: filter.month.getFullYear(),
      month: filter.month.getMonth() + 1,
    },
    onError: onErrorAuto(),
  })

  const { patchWorkPeriod } = useWorkPeriodMigrations()

  const [lockSalaryForUser, { loading: lockLoading }] = useMutation(
    LOCK_CALCULATED_SALARY_FOR_USER,
    {
      onError: onErrorAuto(),
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: WORK_PERIOD_MONTH_REPORT,
          variables: {
            user: userId ?? me.id,
            datetimeStart: formatISO(startDateCalculation),
            datetimeEnd: formatISO(endDateCalculation),
            year: filter.month.getFullYear(),
            month: filter.month.getMonth() + 1,
          },
        },
      ],
    }
  )

  const [deleteSalaryCorrection] = useMutation(
    DELETE_SALARY_CORRECTION_MUTATION
  )
  const [deleteSalaryComment] = useMutation(DELETE_SALARY_COMMENT_MUTATION)

  const allDates = eachDayOfInterval({
    start: startOfWeek(startDateCalculation),
    end: endOfWeek(endDateCalculation),
  })

  const popupWorkPeriodGroup = useMemo(() => {
    return (
      data?.monthReport?.workPeriodGroups.find(
        group => group.id === popupWorkPeriodGroupId
      ) ?? null
    )
  }, [data, popupWorkPeriodGroupId])

  const handleSetFilterStorageStateLoading = useEffectEvent(
    (loading: boolean) => {
      setFilterStorageStateLoading(loading)
    }
  )

  useEffect(() => {
    /* Ensuring the query for ActivityReport is only run once on render. */
    if (filterStorageStateLoading) {
      const loadedFormData = loadStoredFormState()
      if (loadedFormData) updateInitialValues(loadedFormData)

      handleSetFilterStorageStateLoading(false)
    }
  }, [
    filter.month,
    filterStorageStateLoading,
    updateInitialValues,
    loadStoredFormState,
  ])

  const workPeriodGroups = data?.monthReport?.workPeriodGroups

  function updateUrl(id: string | null) {
    if (!id) return
    void navigate(`/work-periods/${id}`, { replace: true })
  }

  const calendarData = allDates.map(date => ({
    date: date,
    weekNumber: date.getDay() === 1 ? format(date, 'w') : null,
    grayed: !isWithinInterval(date, {
      start: startDateCalculation,
      end: endDateCalculation,
    }),
    nodes:
      workPeriodGroups
        ?.filter(group =>
          activeSalaryCode
            ? group.groupedActivitySums.filter(
                sum => sum.name === activeSalaryCode && sum.sum !== '00:00'
              ).length > 0 ||
              group.groupedActivitySumsFloat.filter(
                sum => sum.name === activeSalaryCode && sum.sum !== 0
              ).length > 0
            : true
        )
        .reduce<CalendarNode[]>((acc, cur) => {
          const approvedByDriver =
            cur.approvedByUser || !salaryConfiguration.driverApprove
          const approvedByManager =
            cur.approvedByManager || !salaryConfiguration.managerApprove

          if (
            salaryConfiguration.onlyIncludeApprovedHours &&
            (!approvedByDriver || !approvedByManager) &&
            activeSalaryCode
          ) {
            return acc
          }
          const entryRange = cur.calendarItem
            ? {
                start: parseISO(cur.calendarItem.start),
                end: parseISO(cur.calendarItem.end),
              }
            : cur.workPeriods?.[0]
            ? {
                start: parseISO(cur.workPeriods[0]?.datetimeStart),
                end: parseISO(cur.workPeriods[0]?.datetimeEnd),
              }
            : cur.absenceDay
            ? {
                start: startOfDay(parseISO(cur.absenceDay.date)),
                end: endOfDay(parseISO(cur.absenceDay.date)),
              }
            : null
          if (!entryRange) return acc
          const startingThisDay = isSameDay(date, entryRange.start)
          const endingThisDay = isSameDay(date, entryRange.end)
          const isInRange =
            isWithinInterval(date, {
              start: entryRange.start,
              end: entryRange.end,
            }) ||
            startingThisDay ||
            endingThisDay
          if (isInRange) {
            const activeSalaryCodeSum = cur?.groupedActivitySums.find(
              activitySum => activitySum.name === activeSalaryCode
            )?.sum
            const activeSalaryCodeSumFloat =
              cur?.groupedActivitySumsFloat?.find(
                activitySum => activitySum.name === activeSalaryCode
              )?.sum
            const calendarText = cur.calendarItem?.title ?? ''
            let calendarTimeText = ''
            if (activeSalaryCode && activeSalaryCodeSum) {
              calendarTimeText = enforceHourMinuteString(activeSalaryCodeSum)
            } else if (activeSalaryCode && activeSalaryCodeSumFloat) {
              calendarTimeText = activeSalaryCodeSumFloat.toString()
            } else {
              calendarTimeText = enforceHourMinuteString(
                cur.totalWorkTime ?? ''
              )
            }
            const text =
              calendarText && calendarTimeText && !activeSalaryCode
                ? `${calendarText}: (${calendarTimeText})`
                : cur.absenceDay
                ? `${calendarTimeText}: ${cur.absenceDay.absence.absenceType?.name}`
                : calendarTimeText ?? ''
            let color = theme.colors.green

            if (
              (!approvedByDriver || !approvedByManager) &&
              cur.workPeriods.length !== 0
            ) {
              color = theme.colors.orange
            }
            if (
              !cur.calendarItem &&
              salaryConfiguration.useCalendarForOvertime &&
              cur.workPeriods.length !== 0
            ) {
              color = theme.colors.red8
            }
            if (cur.absenceDay) {
              color = theme.colors.blue400
            }
            acc.push({
              id: cur.calendarItem?.id ?? cur.workPeriods?.[0]?.id ?? '',
              text: text,
              color: color,
              onClick() {
                setPopupWorkPeriodGroupId(cur.id)
              },
            })
          }
          return acc
        }, []) ?? [],
  }))

  const hasOverlappingMonths = data?.monthReport?.overtime?.some(
    activitySum => activitySum.nextMonth
  )

  const overtimeText = (
    <p>
      * {translations.overtimeThatOverlapsMonths}{' '}
      <b onClick={() => updateForm({ month: addMonths(filter.month, 1) })}>
        {translations.nextMonth.toLowerCase()}
      </b>
    </p>
  )

  async function handlePatchWorkPeriod(
    workPeriodId: string,
    values: {
      approvedByUser?: boolean
      approvedByManager?: boolean
      calendarItem?: string | null
    }
  ) {
    const { data } = await patchWorkPeriod({
      variables: {
        id: workPeriodId,
        input: values,
      },
    })
    if (data?.patchWorkPeriod.workPeriod && popupWorkPeriodGroup) {
      const newPopupWorkPeriodGroup = {
        ...popupWorkPeriodGroup,
        workPeriods: popupWorkPeriodGroup.workPeriods.map(workPeriod => {
          if (workPeriod.id === workPeriodId) {
            return {
              ...workPeriod,
              ...data.patchWorkPeriod.workPeriod,
            }
          }
          return workPeriod
        }),
      }
      if ('calendarItem' in values) {
        setPopupWorkPeriodGroupId(null)
      } else {
        setPopupWorkPeriodGroupId(newPopupWorkPeriodGroup.id)
      }
    }
  }

  function handleExitDetails() {
    setPopupWorkPeriodGroupId(null)
  }

  const userTasks = data?.remainingWorkPeriodResolving ?? []

  const remainingWorkPeriodResolvingCount = userTasks.length ?? 0

  const timedeltaRows = useMemo(() => {
    let result = [...(data?.monthReport?.groupedActivitySums ?? [])]
    result = result?.sort((a, b) =>
      (a.salaryId ?? '').localeCompare(b.salaryId ?? '')
    )
    return result?.map(activitySum => {
      const lockedValues =
        data?.salaryBaseByMonth?.entriesWithoutDepartment.filter(entry => {
          const local = entry?.salaryId
          const el = activitySum.salaryId
          return local === el
        })
      const lockedValue = lockedValues?.length
        ? parseFloatToTimestamp(lockedValues[0]?.amount ?? 0) ?? '00:00'
        : '00:00'

      return (
        <StyledRow
          isActive
          isAbsence={false}
          onClick={() => {}}
          onMouseEnter={() => {
            setActiveSalaryCode(activitySum.name)
          }}
          key={`${filter.user ?? ''}${activitySum.id}`}
        >
          <td>{activitySum?.name}</td>
          <td>{enforceHourMinuteString(activitySum?.sum)}</td>
          {data?.monthReport.isLocked && <td>{lockedValue}</td>}
        </StyledRow>
      )
    })
  }, [
    data?.monthReport,
    data?.salaryBaseByMonth?.entriesWithoutDepartment,
    filter.user,
  ])

  async function handleDeleteCorrection(salaryCorrectionId: string) {
    const { data } = await confirm(
      '',
      translations.doYouWishToDeleteSalaryCorrection
    )
    if (!data) return
    void deleteSalaryCorrection({
      variables: {
        id: salaryCorrectionId,
      },
    })
    await refetch()
  }

  const correctionRows =
    data?.monthReport?.salaryCorrections?.map(correction => {
      return (
        <StyledRow
          isActive
          isAbsence={false}
          onClick={() => {}}
          onMouseEnter={() => {
            setActiveSalaryCode(correction.salaryCode.name)
          }}
          key={`${filter.user ?? ''}${correction.id}`}
        >
          <td>
            <div className="flex space-between">
              <div
                className="w-full text-ellipsis"
                title={correction.salaryCode.name}
              >
                {truncate(correction.salaryCode.name, 20)}
              </div>
              <div className="flex gap-1">
                <IconTooltip
                  icon={{
                    icon: 'user-circle',
                    type: 'regular',
                    style: { color: theme.colors.primary },
                  }}
                >
                  {correction.createdBy
                    ? correction.createdBy.fullName
                    : translations.unknownUser}
                </IconTooltip>
                <IconTooltip
                  icon={{
                    icon: 'clock',
                    type: 'regular',
                    style: { color: theme.colors.primary },
                  }}
                >
                  {format(parseISO(correction.createdAt), 'dd.MM.yyyy HH:mm')}
                </IconTooltip>
              </div>
            </div>
          </td>
          <td>{correction.amount}</td>
          <td className="text-right flex items-center gap-2 justify-end">
            {!loading && (
              <SalaryCorrectionDialog
                correction={correction}
                userId={filter.user ?? me.id}
                year={filter.month.getFullYear()}
                month={filter.month.getMonth() + 1}
                onSubmit={() => refetch()}
              />
            )}
            <Button
              size="sm"
              onClick={() => handleDeleteCorrection(correction.id)}
            >
              <FAIcon icon="times" />
            </Button>
          </td>
        </StyledRow>
      )
    }) ?? []

  async function handleDeleteComment(salaryCommentId: string) {
    const { data } = await confirm(
      '',
      translations.doYouWishToDeleteSalaryComment
    )
    if (!data) return
    void deleteSalaryComment({
      variables: {
        id: salaryCommentId,
      },
    })
    await refetch()
  }

  const commentRows =
    data?.allSalaryComments?.map(comment => {
      return (
        <StyledRow
          isActive
          isAbsence={false}
          onClick={() => {}}
          key={`${filter.user ?? ''}${comment.id}`}
        >
          <td colSpan={2}>
            <div className="flex space-between">
              <div className="w-full text-ellipsis" title={comment.value}>
                {truncate(comment.value, 20)}
              </div>
              <div className="flex gap-1">
                <IconTooltip
                  icon={{
                    icon: 'user-circle',
                    type: 'regular',
                    style: { color: theme.colors.primary },
                  }}
                >
                  {comment.createdBy
                    ? comment.createdBy.fullName
                    : translations.unknownUser}
                </IconTooltip>
                <IconTooltip
                  icon={{
                    icon: 'clock',
                    type: 'regular',
                    style: { color: theme.colors.primary },
                  }}
                >
                  {format(parseISO(comment.createdAt), 'dd.MM.yyyy HH:mm')}
                </IconTooltip>
              </div>
            </div>
          </td>
          <td className="text-right flex items-center gap-2 justify-end">
            {!loading &&
              (!comment.createdBy || comment.createdBy.id === me.id) && (
                <SalaryCommentDialog
                  comment={comment}
                  userId={filter.user ?? me.id}
                  year={filter.month.getFullYear()}
                  month={filter.month.getMonth() + 1}
                  onSubmit={() => refetch()}
                />
              )}
            <Button size="sm" onClick={() => handleDeleteComment(comment.id)}>
              <FAIcon icon="times" />
            </Button>
          </td>
        </StyledRow>
      )
    }) ?? []

  const floatRows = useMemo(
    () =>
      data?.monthReport?.groupedActivitySumsFloat?.map(activitySum => {
        const lockedValues =
          data?.salaryBaseByMonth?.entriesWithoutDepartment.filter(entry => {
            const local = entry?.salaryId
            const el = activitySum.salaryId
            return local === el
          })
        const lockedValue = lockedValues?.length
          ? lockedValues[0]?.amount ?? 0
          : '0'

        return (
          <StyledRow
            key={`${filter.user ?? ''}${activitySum.id}`}
            isActive
            isAbsence={false}
            onClick={() => {}}
            onMouseEnter={() => setActiveSalaryCode(activitySum.name)}
          >
            <td>{activitySum?.name}</td>
            <td>{activitySum?.sum}</td>
            {data?.monthReport.isLocked && <td>{lockedValue}</td>}
          </StyledRow>
        )
      }),
    [
      data?.monthReport,
      data?.salaryBaseByMonth?.entriesWithoutDepartment,
      filter.user,
    ]
  )

  return (
    <ModuleWrapper
      title={
        <span>
          {translations.workOverview}
          <span className="ml-1">
            ({format(startDateCalculation, 'dd. MMM')} -{' '}
            {format(endDateCalculation, 'dd. MMM')})
          </span>
        </span>
      }
      page={PageType.WORK_PERIODS}
      loading={loading}
    >
      {canSeeOthers && userTasks && remainingWorkPeriodResolvingCount > 0 && (
        <ApprovalCard
          onClick={() => setRemainingApprovalOpen(!remainingApprovalOpen)}
        >
          <FlexWarning>
            <Icon icon="exclamation-triangle" color="orange" />
            <p>
              <b>
                {remainingWorkPeriodResolvingCount === 1
                  ? translations.oneUserNeedsApproval
                  : translations.nUsersNeedApproval({
                      n: remainingWorkPeriodResolvingCount,
                    })}
              </b>
            </p>
          </FlexWarning>
          {remainingApprovalOpen && (
            <UserLinks>
              {userTasks.map(userRemainingCount => (
                <UserLink
                  key={userRemainingCount.user.id}
                  onClick={evt => {
                    evt.stopPropagation()
                    evt.preventDefault()
                    updateForm({ user: userRemainingCount.user.id })
                    setRemainingApprovalOpen(false)
                  }}
                >
                  {userRemainingCount.user.fullName} (
                  {translations.nWorkPeriods({
                    n: userRemainingCount.numTasks,
                  })}
                  )
                </UserLink>
              ))}
            </UserLinks>
          )}
        </ApprovalCard>
      )}

      <Controls>
        <section>
          {canSeeOthers && (
            <Field>
              <label>{translations.user}</label>
              <UserSelect
                value={filter.user}
                grayed
                width="100%"
                minWidth={isMobileOnly ? '0' : '260px'}
                height="3.875rem"
                onChange={user => updateForm({ user: user ?? me.id })}
                filterLeaderGroup
              />
            </Field>
          )}

          <Field>
            <label>{translations.month}</label>
            <MonthPicker
              value={filter.month}
              futureYears={0}
              minWidth={isMobileOnly ? '0' : '380px'}
              width={isMobileOnly ? '100%' : undefined}
              maxWidth={isMobileOnly ? '100%' : '420px'}
              onChange={handler('month')}
            />
          </Field>

          {canSeeOthers && (
            <Field>
              <label>{translations.lock}</label>
              <Button
                onClick={() =>
                  filter.user &&
                  lockSalaryForUser({
                    variables: {
                      user: filter.user,
                      datetimeStart: formatISO(startDateCalculation),
                      datetimeEnd: formatISO(endDateCalculation),
                    },
                  })
                }
              >
                {lockLoading ? <Loader.Spinner /> : translations.lock}
              </Button>
            </Field>
          )}
        </section>
      </Controls>

      {!popupWorkPeriodGroup && (
        <CalendarAndTable>
          <CalendarWrapper>
            <Calendar data={calendarData} />
            {salaryConfiguration.usePlannerForOvertime && (
              <CalendarColorExplanation />
            )}
          </CalendarWrapper>
          <TableArea>
            <StyledTable
              headerMargin
              rowHoverBackground="gray10"
              onMouseLeave={() => setActiveSalaryCode(null)}
            >
              <thead>
                <tr>
                  <th>{translations.salaryCode}</th>
                  <th>{translations.amount}</th>
                  <th>
                    {data?.monthReport.isLocked ? translations.lockedValue : ''}
                  </th>
                </tr>
              </thead>
              <tbody>
                {timedeltaRows?.map(row => (
                  <React.Fragment key={`timedelta-${row.key}`}>
                    {row}
                  </React.Fragment>
                ))}
                {floatRows?.map(row => (
                  <React.Fragment key={`float-${row.key}`}>
                    {row}
                  </React.Fragment>
                ))}
              </tbody>
              <Activity mode={filter.user ? 'visible' : 'hidden'}>
                <thead>
                  <tr>
                    <th>{translations.corrections}</th>
                    <th>{translations.amount}</th>
                    <th className="text-right">
                      <SalaryCorrectionDialog
                        userId={filter.user ?? me.id}
                        year={filter.month.getFullYear()}
                        month={filter.month.getMonth() + 1}
                        onSubmit={() => refetch()}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {correctionRows?.map(row => (
                    <React.Fragment key={row.key}>{row}</React.Fragment>
                  ))}
                </tbody>
              </Activity>
              <Activity mode={filter.user ? 'visible' : 'hidden'}>
                <thead>
                  <tr>
                    <th colSpan={2}>{translations.comments}</th>
                    <th className="text-right">
                      <SalaryCommentDialog
                        userId={filter.user ?? me.id}
                        year={filter.month.getFullYear()}
                        month={filter.month.getMonth() + 1}
                        onSubmit={() => refetch()}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {commentRows?.map(row => (
                    <React.Fragment key={row.key}>{row}</React.Fragment>
                  ))}
                </tbody>
              </Activity>
            </StyledTable>

            <StyledTable headerMargin rowHoverBackground="gray10">
              <thead>
                <tr>
                  <th>{translations.overtime}</th>
                  <th>{translations.amount}</th>
                </tr>
              </thead>

              <tbody>
                {data?.monthReport?.overtime?.map((activitySum, i) => (
                  <StyledRow
                    key={`overtime-sums-${i}`}
                    isActive
                    isAbsence={false}
                    onClick={() => {}}
                  >
                    <td>
                      {activitySum.name}
                      {activitySum.nextMonth && '*'}
                    </td>
                    <td>{enforceHourMinuteString(activitySum?.sum)}</td>
                  </StyledRow>
                ))}
              </tbody>
            </StyledTable>
            <StyledTable headerMargin rowHoverBackground="gray10">
              <thead>
                <tr>
                  <th>{translations.undertime}</th>
                  <th>{translations.amount}</th>
                </tr>
              </thead>

              <tbody>
                {data?.monthReport?.undertime?.map(activitySum => (
                  <StyledRow
                    key={`undertime-${activitySum.id}`}
                    isActive
                    isAbsence={false}
                    onClick={() => {}}
                  >
                    <td>
                      {activitySum.name}
                      {activitySum.nextMonth && '*'}
                    </td>
                    <td>{enforceHourMinuteString(activitySum?.sum)}</td>
                  </StyledRow>
                ))}
              </tbody>
            </StyledTable>

            {!loading && hasOverlappingMonths && overtimeText}
          </TableArea>
        </CalendarAndTable>
      )}

      <div>
        {popupWorkPeriodGroup &&
          (!salaryConfiguration.usePlannerForOvertime &&
          popupWorkPeriodGroup.workPeriods[0] ? (
            <WorkPeriodDetails
              workPeriod={popupWorkPeriodGroup.workPeriods[0]}
              userId={filter.user ?? me.id}
              patchWorkPeriod={(workPeriodId, values) =>
                handlePatchWorkPeriod(workPeriodId, values)
              }
              onClose={handleExitDetails}
              salaryCalendarSettings={salaryConfiguration}
              refetch={() => refetch()}
            />
          ) : popupWorkPeriodGroup.absenceDay ? (
            <AbsenceDetailsSalary
              absenceId={popupWorkPeriodGroup.absenceDay.absence.id}
              onClose={handleExitDetails}
            />
          ) : (
            <WorkPeriodGroupDetails
              onAttachModalClose={() => {}}
              group={popupWorkPeriodGroup}
              userId={filter.user ?? me.id}
              patchWorkPeriod={(workPeriodId, values) =>
                handlePatchWorkPeriod(workPeriodId, values)
              }
              onClose={handleExitDetails}
              refetch={() => refetch()}
            />
          ))}
      </div>
      {!popupWorkPeriodGroup && (
        <AddonOverview
          userId={filter.user ?? me.id}
          datetimeStart={startDateCalculation}
          datetimeEnd={endDateCalculation}
        />
      )}
    </ModuleWrapper>
  )
}
